import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Row, Typography, Button, Select } from "antd";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";

import BackButton from "components/BackButton";
import { TextInput, TextEditor, MultiImageUpload, ContestCandidateImageUpload } from "components/common";
import { createCandidateRequest } from "providers/CandidateProvider/slice";
import { getActiveContestsRequest } from "providers/ContestProvider/slice";
import Helper from "utils/helpers";
import "./styles.less";

const { Option } = Select;

const validationSchema = Yup.object().shape({
  contestId: Yup.string().required("Cuộc thi là bắt buộc"),
  fullName: Yup.string().required("Họ tên là bắt buộc"),
  contestNumber: Yup.string().required("Số báo danh là bắt buộc"),
  hometown: Yup.string().required("Quê quán là bắt buộc"),
  currentResidence: Yup.string().required("Nơi ở hiện tại là bắt buộc"),
  message: Yup.string().required("Thông điệp là bắt buộc"),
  introduction: Yup.string().required("Giới thiệu là bắt buộc"),
  informationDetail: Yup.string().required("Thông tin chi tiết là bắt buộc"),
  videoUrl: Yup.string()
    .url("URL video không hợp lệ"),
  images: Yup.array()
    .min(1, "Ít nhất 1 ảnh là bắt buộc")
    .required("Ảnh là bắt buộc"),
});

const CandidateCreate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activeContests = useSelector(
    (state) => state.contestSlice.activeContests || []
  );

  useEffect(() => {
    // Load active contest list for dropdown
    dispatch(getActiveContestsRequest({ limit: 100 }));
  }, [dispatch]);

  const handleSubmit = async (values) => {
    const candidateData = {
      contestId: values.contestId,
      fullName: values.fullName,
      contestNumber: values.contestNumber,
      hometown: values.hometown,
      currentResidence: values.currentResidence,
      message: values.message,
      introduction: values.introduction,
      informationDetail: values.informationDetail,
      images: values.images.map((img) =>
        typeof img === "string"
          ? { url: img }
          : img.url
            ? { url: img.url }
            : img
      ),
    };

    // Only add videoUrl if it has a value
    if (values.videoUrl && values.videoUrl.trim() !== "") {
      candidateData.videoUrl = values.videoUrl;
    }

    dispatch(createCandidateRequest(candidateData))
      .then((data) => {
        Helper.toastr(
          "Tạo thí sinh thành công",
          "Thí sinh đã được tạo thành công",
          "success"
        );

        // Navigate back to candidate list
        navigate("/voting/candidate_list", {
          state: { canGoBack: true },
          replace: true,
        });
      })
      .catch((error) => {
        console.error("Create candidate error:", error);
        Helper.toastr(
          "Tạo thí sinh thất bại",
          "Có lỗi xảy ra khi tạo thí sinh. Vui lòng thử lại.",
          "error"
        );
      });
  };

  const initialValues = {
    contestId: "",
    fullName: "",
    contestNumber: "",
    hometown: "",
    currentResidence: "",
    message: "",
    introduction: "",
    informationDetail: "",
    videoUrl: "",
    images: [],
  };

  return (
    <div className="candidate-create-wrapper">
      <Row className="candidate-create-header" justify="start" align="middle">
        <BackButton />
        <Typography.Title level={4}>Tạo thí sinh</Typography.Title>
      </Row>
      <div className="create-wrapper">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ handleSubmit: formikHandleSubmit, values, setFieldValue }) => (
            <Form>
              <div className="candidate-form-container">
                {/* Basic Information */}
                <div className="form-section">
                  <div className="section-title">Thông tin cơ bản</div>

                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Cuộc thi:</div>
                      <Select
                        size="large"
                        placeholder="Chọn cuộc thi"
                        value={values.contestId}
                        onChange={(value) => setFieldValue("contestId", value)}
                        style={{ width: "100%" }}
                        loading={!activeContests.length}
                      >
                        {activeContests.map((contest) => (
                          <Option key={contest._id} value={contest._id}>
                            {contest.name}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Họ tên:</div>
                      <TextInput name="fullName" size="large" required />
                    </div>
                    <div className="form-item">
                      <div className="item-title">Số báo danh:</div>
                      <TextInput name="contestNumber" size="large" required />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Quê quán:</div>
                      <TextInput name="hometown" size="large" required />
                    </div>
                    <div className="form-item">
                      <div className="item-title">Nơi ở hiện tại:</div>
                      <TextInput
                        name="currentResidence"
                        size="large"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Message */}
                <div className="form-section">
                  <div className="section-title">Thông điệp</div>
                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Thông điệp của thí sinh:</div>
                      <TextEditor
                        name="message"
                        placeholder="Nhập thông điệp của thí sinh..."
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Introduction */}
                <div className="form-section">
                  <div className="section-title">Giới thiệu</div>
                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Giới thiệu của thí sinh:</div>
                      <TextEditor
                        name="introduction"
                        placeholder="Nhập giới thiệu của thí sinh..."
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Information Detail */}
                <div className="form-section">
                  <div className="section-title">Thông tin chi tiết</div>
                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Thông tin chi tiết của thí sinh:</div>
                      <TextEditor
                        name="informationDetail"
                        placeholder="Nhập thông tin chi tiết của thí sinh..."
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Video URL */}
                <div className="form-section">
                  <div className="section-title">Video</div>
                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">URL Video:</div>
                      <TextInput
                        name="videoUrl"
                        size="large"
                        placeholder="https://example.com/video.mp4"
                      />
                    </div>
                  </div>
                </div>

                {/* Images */}
                <div className="form-section">
                  <div className="section-title">Ảnh thí sinh</div>
                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Ảnh thí sinh:</div>
                      <ContestCandidateImageUpload
                        name="images"
                        setFieldValue={setFieldValue}
                        required
                        maxImages={4}
                        formType="candidate"
                        helpText="Tải lên ảnh của thí sinh (tối đa 4 ảnh, khuyến nghị ảnh chân dung)"
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="form-actions">
                  <Button size="large" type="primary" htmlType="submit">
                    Tạo thí sinh
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default CandidateCreate;
